import * as React from 'react';

import type { ExtendedColorValue } from '@acceligentllc/shared/enums/color';

import { useToggle } from 'af-utils/react.util';

import ColorSquare from 'af-components/ColorSquare';
import ConfirmationModal from 'af-components/ConfirmationModal';
import Tooltip from 'af-components/Tooltip';

interface OwnProps<T> {
	labelKey?: string;
	item: T;
	isLocked?: boolean;
	isTransparent?: boolean;
	skipConfirmationModal?: boolean;
	modalTitle?: string;
	index?: number;
	color?: Nullable<ExtendedColorValue>;
	modalBody?: (original: T) => React.JSX.Element;
	removeItem?: (index?: number) => void;
	renderItem?: (item: T) => React.JSX.Element | string;
	renderPrefix?: (item: T) => React.ReactNode;
	onClick?: () => void;
}

type Props<T> = OwnProps<T>;

function Pill<T,>(props: React.PropsWithChildren<Props<T>>) {
	const {
		isTransparent = false,
		onClick,
		labelKey,
		skipConfirmationModal = false,
		item,
		modalTitle,
		modalBody,
		color,
		isLocked = false,
		removeItem,
		renderItem,
		renderPrefix,
		index,
	} = props;

	const {
		value: showModal,
		setToTrue: openModal,
		setToFalse: hideModal,
	} = useToggle(false);

	const onRemoveItem = React.useCallback(() => {
		removeItem?.(index);
	}, [index, removeItem]);

	let className = 'pills__item';
	className = isTransparent ? `${className} pills__item--transparent` : className;
	className = onClick ? `${className} pills__item--clickable` : className;

	const showConfirmationModal = !skipConfirmationModal && !isLocked;

	return (
		<span className={className}>
			<span className="pills__label" onClick={onClick}>
				<span className="pills__label" onClick={onClick}>
					{renderPrefix?.(item)}
					{color && <ColorSquare color={color} />}
					{labelKey ? item[labelKey] : renderItem?.(item)}
				</span>
			</span>
			{
				!isLocked && removeItem &&
				<Tooltip message="Delete">
					<span
						className="icon-close"
						onClick={skipConfirmationModal ? onRemoveItem : openModal}
					/>
				</Tooltip>
			}
			{showConfirmationModal &&
				<ConfirmationModal
					body={modalBody?.(item)}
					closeModal={hideModal}
					confirmAction={onRemoveItem}
					confirmText="Remove"
					modalStyle="danger"
					showModal={showModal}
					title={modalTitle}
				/>
			}
		</span>
	);
}

export default React.memo(Pill) as typeof Pill;
